
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiRequest } from '@/utils/request';
import { cn } from '@/utils';
import { GridStack } from 'gridstack';
import 'gridstack/dist/gridstack.min.css';
import { Background, Button, Container, Dialog, IconButton } from '@/components/ui';
import { ContentHeader } from '@/components/business';
import { NewValueWidgetData } from './Content/NewValueWidgetData';
import { NewValueWidgetGeneral } from './Content/NewValueWidgetGeneral';
import { NewWidgetType } from './Content/NewWidgetType';
import { Widget } from './Content/Widget';
import { Wizard } from '@/components/business/Wizard';

const schema = z.object({
    type: z.string(),
    subtitle: z.string().optional(),
    title: z.string(),
    id: z.string().optional(),
    source: z.string().optional(),
    legend: z.string().optional(),
    graphical: z.object({
        visible: z.boolean().optional(),
        palette: z.string(),
        icon: z.string().optional(),
        size: z.object({ h: z.number(), w: z.number() }).optional(),
        position: z.object({ x: z.number(), y: z.number() }).optional()
    }),
    data: z.object({
        indicatorId: z.string().optional(),
        period: z.string().optional(),
        unitScale: z.number().optional(),
        decimals: z.string().optional()
    })
});

const buildDefaultValues = () => ({
    type: 'value_card',
    subtitle: '',
    title: '',
    id: '',
    source: 'indicator',
    legend: '',
    graphical: {
        visible: true,
        palette: '',
        icon: 'circleInfo',
        size: { h: 2, w: 12 },
        position: { x: 0, y: 0 }
    },
    data: {
        indicatorId: '',
        period: '',
        unitScale: -1,
        decimals: '2'
    }
});

export default function CustomDashboard () {
    const
        initialWidgets = WiseMetering.currentOrganization.get('dashboard')?.widgets || [],
        organizationSubdomain = WiseMetering.currentOrganization.get('subdomain');

    const
        [isCreating, setIsCreating] = useState(false),
        [isEditing, setIsEditing] = useState(false),
        [savedWidgets, setSavedWidgets] = useState(initialWidgets),
        [widgets, setWidgets] = useState(initialWidgets);

    const
        gridRef = useRef(null),
        grid = useRef(null);

    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues: buildDefaultValues(),
        mode: 'onBlur'
    });

    const steps = [
        {
            Component: NewWidgetType,
            title: i18next.t('dashboard.custom_dashboard.type_title'),
            description: i18next.t('dashboard.custom_dashboard.type_description'),
            isValid: data => Boolean(data.type)
        },
        {
            Component: NewValueWidgetData,
            title: i18next.t('dashboard.custom_dashboard.data_title'),
            description: i18next.t('dashboard.custom_dashboard.data_description'),
            isValid: data => Boolean(data.data?.indicatorId) && Boolean(data.data?.period)
        },
        {
            Component: NewValueWidgetGeneral,
            title: i18next.t('dashboard.custom_dashboard.general_title'),
            description: i18next.t('dashboard.custom_dashboard.general_description'),
            isValid: data => Boolean(data.title?.trim()) && Boolean(data.graphical?.palette)
        }
    ];

    const addNewWidget = () => {
        setIsCreating(true);
    };

    const cancelEditing = () => {
        if (!grid.current) {
            return;
        }

        // Clean gridstack and React state to restore previous layout
        grid.current.off('change');
        grid.current.destroy(false);
        grid.current = null;

        // Rebuild GridStack
        setTimeout(() => {
            grid.current = GridStack.init(
                {
                    column: 12,
                    cellHeight: 100,
                    float: false,
                    resizable: { handles: 'all' },
                    draggable: { handle: '.widget-header' },
                },
                gridRef.current
            );

            setWidgets(savedWidgets);

            grid.current.setStatic(true);
            grid.current.enableMove(false);
            grid.current.enableResize(false);

            setIsEditing(false);
        }, 0);
    };

    const handleSubmit = async (data) => {
        const nextY = Math.max(0, ...widgets.map(w => w.graphical.position.y + w.graphical.size.h));

        const newWidget = {
            ...data,
            id: `${Date.now()}-${organizationSubdomain}`,
            graphical: {
                ...data.graphical,
                position: { x: 0, y: nextY },
                size: { w: 3, h: 2 },
            }
        };

        const newWidgets = [...widgets, newWidget];

        setWidgets(newWidgets);
        setIsCreating(false);
        form.reset(buildDefaultValues());

        // Allow GridStack to process the new widget
        setTimeout(() => {
            grid.current?.makeWidget(
                gridRef.current.querySelector(`[gs-id="${newWidget.id}"]`)
            );
        }, 0);
    };

    const handleCancelCreate = () => {
        setIsCreating(false);
        form.reset(buildDefaultValues());
    };

    const removeWidget = (id) => {
        setWidgets(prev =>
            prev.map(w => w.id === id ? { ...w, deleted: true } : w)
        );
    };

    const saveDashboard = async () => {
        const submitData = {
            dashboard: {
                widgets: widgets.filter(w => !w.deleted)
            }
        };

        try {
            const payload = submitData;
            const url = `/organizations/${WiseMetering.currentOrganization.get('id')}/actions/update_dashboard`;
            const response = await apiRequest(url, 'PUT', payload);

            if (response.data.dashboard) {
                setWidgets(response.data.dashboard.widgets);
                setSavedWidgets(response.data.dashboard.widgets); // update backup
            }

            setIsEditing(false);
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to save dashboard');
        }
    };

    const startEditing = () => {
        setSavedWidgets(widgets); // backup current state
        setIsEditing(true);
    };

    useEffect(() => {
        if (!gridRef.current) {
            return;
        }

        // Init GridStack
        grid.current = GridStack.init(
            {
                column: 12,     // grid spaces per row
                cellHeight: 100,
                float: false,
                resizable: { handles: 'all' },
                draggable: { handle: '.widget-header' },
            },
            gridRef.current
        );

        // Listen for layout changes
        grid.current.on('change', (_, items) => {
            if (!items) {
                return;
            }

            // Extract ONLY the serializable layout values
            const clean = items.map((i) => ({
                id: i.id,
                x: i.x,
                y: i.y,
                w: i.w,
                h: i.h
            }));

            setWidgets(prev =>
                prev.map(w => {
                    const updated = clean.find(i => i.id === w.id);
                    if (!updated) {
                        return w;
                    }

                    return {
                        ...w,
                        graphical: {
                            ...w.graphical,
                            position: { x: updated.x, y: updated.y },
                            size: { w: updated.w, h: updated.h }
                        }
                    };
                })
            );
        });

        return () => grid.current?.destroy(false);
    }, [widgets.length]);

    useEffect(() => {
        if (!grid.current) {
            return;
        }

        if (isEditing) {
            grid.current.setStatic(false);
            grid.current.enableMove(true);
            grid.current.enableResize(true);

            // Inject + widget into GridStack if not already present in the DOM
            const existing = gridRef.current.querySelector('[gs-id="add-widget"]');
            if (!existing) {
                // 1. Create real DOM node
                const wrapper = document.createElement('div');
                wrapper.className = 'grid-stack-item';
                wrapper.style.padding = '8px';

                const inner = document.createElement('div');
                inner.className = `
                    tw-flex tw-items-center tw-justify-center
                    tw-border tw-border-dashed tw-border-gray-400
                    tw-rounded-xl tw-h-full tw-bg-muted hover:tw-bg-gray-200
                    tw-cursor-pointer tw-transition
                `;

                const span = document.createElement('span');
                span.className = 'tw-text-4xl tw-text-muted-foreground';
                span.innerText = '+';

                inner.appendChild(span);
                wrapper.appendChild(inner);

                grid.current.addWidget({
                    id: 'add-widget',
                    x: 0,
                    y: 9999, // forces it to the bottom
                    w: 3,
                    h: 2,
                    el: wrapper
                });

                inner.addEventListener('click', addNewWidget);
            }
        } else {
            // Disable editing
            grid.current.setStatic(true);
            grid.current.enableMove(false);
            grid.current.enableResize(false);

            // Remove the + widget
            const el = gridRef.current.querySelector('[gs-id="add-widget"]');
            if (el) {
                grid.current.removeWidget(el);
            }
        }
    }, [isEditing]);

    return (
        <Background>
            <Container variant="row">
                <div className="tw-flex-1 tw-min-w-0">
                    <Dialog
                        open={isCreating}
                        onClose={handleCancelCreate}
                        size="large"
                        height="full"
                        closable={false}
                    >
                        <Wizard
                            form={form}
                            onCancel={handleCancelCreate}
                            onClose={handleCancelCreate}
                            onSubmit={handleSubmit}
                            steps={steps}
                        />
                    </Dialog>
                    <ContentHeader
                        description={i18next.t('dashboard.custom_dashboard.header_description')}
                        icon="globe"
                        title={`Dashboard ${WiseMetering.currentOrganization.get('name')}`}
                    >
                        {WiseMetering.Permissions.hasRolePermissions('customDashboard', 'update') && (
                            isEditing ? (
                                <>
                                    <Button size="small" onClick={saveDashboard}>
                                        {i18next.t('ui.buttons.save')}
                                    </Button>
                                    <div className="tw-ml-2 tw-inline-block">
                                        <Button size="small" tone="destructive" onClick={cancelEditing}>
                                            {i18next.t('ui.buttons.cancel')}
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <Button size="small" onClick={startEditing}>
                                    {i18next.t('ui.buttons.edit')}
                                </Button>
                            )
                        )}
                    </ContentHeader>
                    <div
                        className={cn(
                            'grid-stack tw-p-4 tw-gap-4 tw-min-h-dashboard-grid',
                            isEditing ? 'tw-mt-4' : 'tw-mt-2',
                            isEditing && 'dashboard-edit-grid'
                        )}
                        ref={gridRef}
                    >
                        {widgets.map((widget) => (
                            !widget.deleted && (
                                <div
                                    key={widget.id}
                                    className="grid-stack-item relative"
                                    gs-id={widget.id}
                                    gs-x={widget.graphical.position?.x ?? undefined}
                                    gs-y={widget.graphical.position?.y ?? undefined}
                                    gs-w={widget.graphical.size.w}
                                    gs-h={widget.graphical.size.h}
                                >
                                    <div className="tw-relative tw-h-full tw-w-full">
                                        {/* Widget content */}
                                        <Widget widget={widget}/>

                                        {/* Centered button only in editing mode */}
                                        {isEditing && (
                                            <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center pointer-events-auto">
                                                <IconButton appearance="solid" aria-label={i18next.t('ui.actions.remove', 'Remove widget')} icon="delete" onClick={() => removeWidget(widget.id)} tone="destructive" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        ))}
                    </div>
                </div>
            </Container>
        </Background>
    );
}
